import adapter from 'webrtc-adapter';

import { Effect } from '../event/effect';

import { ERTCEventName, ERTCHookName, ERTCEventType } from '../types/rtc'
import { washWebrtcAnswerSdp } from '../translate/network';

import { type IWsSend, type IWsEffectCallback, EWSEventName } from '../types/socket';

export class DeviceRTC extends Effect {

  wsSend: IWsSend;
  wsEffect: IWsEffectCallback;

  isEffectedIceCandidate = false;
  interval: NodeJS.Timeout | null = null;
  rtc: RTCPeerConnection | null = null;
  dataChannel: RTCDataChannel | null = null;
  prevUpdateStream = 0;
  videoForcei = 0;

  constructor (wsSend: IWsSend, wsEffect: IWsEffectCallback) {
    super();
    this.wsSend = wsSend;
    this.wsEffect = wsEffect;
  }

  init () {
    // default
    this.rtc = new RTCPeerConnection();
    this.rtc.addTransceiver('video', { direction: 'recvonly' })
    this.rtc.addTransceiver('audio', { direction: 'recvonly' })

    this.listenStream();
    this.initDataChannel();
    if (this.interval) clearInterval(this.interval);
    this.interval = setInterval(this.getRtcStatus.bind(this), 2000);
    this.listenIceCandidate();
  }

  listenStream () {
    if (!this.rtc) return;
    this.rtc.addEventListener('track', (event) => {
      this.hookEvent(ERTCHookName.STREAM, event.streams[0]);
    })
    this.rtc.addEventListener('addstream', (event: any) => {
      this.hookEvent(ERTCHookName.STREAM, event.stream);
    })
    this.rtc.addEventListener('addtrack', (event) => {
      this.hookEvent(ERTCHookName.STREAM, event);
    });
  }

  listenIceCandidate () {
    if (!this.rtc) return;
    this.rtc.addEventListener('icecandidate', (evt) => {
      if (evt.candidate && !this.isEffectedIceCandidate) {
        this.isEffectedIceCandidate = true;
        this.hookEvent(ERTCHookName.CANDIDATE, evt.candidate)
        this.wsSend(EWSEventName.CANDIDATE, evt.candidate);
      }
    })
  }

  initDataChannel () {
    if (!this.rtc) return;
    this.dataChannel = this.rtc.createDataChannel('MessageChannel', {
      maxRetransmits: 3000,
      protocol: 'udp',
      ordered: true,
    });
    this.dataChannel.onopen = (event) => {
      this.hookEvent(ERTCHookName.DATA_CHANNEL_OPEN, event)
    }
  }

  async createOffer () {
    if (!this.rtc) return;
    const sdp = await this.rtc.createOffer();
    await this.rtc.setLocalDescription(sdp);
    return sdp;
  }

  async setRemoteDescription (answer: RTCSessionDescriptionInit, deviceIPInfo: { ip: string; port: number; }) {
    if (!this.rtc || !answer.sdp) return;
    answer.sdp = washWebrtcAnswerSdp(answer.sdp, deviceIPInfo.ip, deviceIPInfo.port)
    await this.rtc.setRemoteDescription(answer)
  }

  async getRtcStatus () {
    if (!this.rtc) return;
    const status = await this.rtc.getStats();
    status.forEach((report) => {
      Object.keys(report).forEach((statName) => {
        if (statName === 'currentRoundTripTime') {
          this.hookEvent(ERTCHookName.RTT, report.currentRoundTripTime * 1000);
          return;
        }
      })

      // hook ws ping data
      if(report.jitter && report.kind === 'video') {
        this.hookEvent(ERTCHookName.STATUS_DATA, report);
        this.wsSend(EWSEventName.PING, report);
      }
    })

    // Stuttering Frames
    if (this.prevUpdateStream !== 0 && (+new Date() - this.prevUpdateStream) > 8000) {
      console.info(`video stopppppppppppppppppppppppppppppppppppped`);
      this.wsSend(EWSEventName.FORCEI, {});
      this.videoForcei += 1;
      if (this.videoForcei >= 10) {
        this.close();
        // this.init();
        this.hookEvent(ERTCHookName.NETWORK_ERROR, {});
      }
    }
  }

  videoUpdateTimeout () {
    this.prevUpdateStream = +new Date();
  }

  dataChannelSend<T> (eventName: ERTCEventName, data: T) {
    this.dataChannel?.send(JSON.stringify({ eventType: ERTCEventType.CLIENT, eventName, data }));
  }

  addCandidate (candidate: string) {
    if (!this.rtc) return;
    const iceCandidate = new RTCIceCandidate({
      sdpMid: '0',
      sdpMLineIndex: 0,
      candidate
    });
    this.rtc.addIceCandidate(iceCandidate);
  }

  close () {
    this.rtc?.close();
    this.prevUpdateStream = 0;
    this.interval && clearInterval(this.interval);
  }
}

export function deviceWebRTC (wsSend: IWsSend, wsEffect: IWsEffectCallback) {
  const instance = new DeviceRTC(wsSend, wsEffect);
  return {
    getRTCInstance: () => instance.rtc,
    close: instance.close.bind(instance),
    init: instance.init.bind(instance),
    rtcEffect: instance.effect.bind(instance),
    createOffer: instance.createOffer.bind(instance),
    dataChannelSend: instance.dataChannelSend.bind(instance),
    videoUpdateTimeout: instance.videoUpdateTimeout.bind(instance),
    setRemoteDescription: instance.setRemoteDescription.bind(instance),
    addCandidate: instance.addCandidate.bind(instance)
  }
}
